<template>
  <div class="order-detail-tab order-detail-tab--payment">
    <template v-if="!orderId">
      <a-empty description="暂无订单数据" />
    </template>
    <template v-else-if="loading">
      <a-spin tip="加载中..." />
    </template>
    <template v-else-if="!hasData">
      <a-empty description="暂无数据" />
    </template>
    <div v-else class="tab-content">
      <a-row :gutter="16">
        <a-col :span="10">
          <div class="payment-block-title">支付项</div>
          <a-table
            v-if="list.length > 0"
            :columns="columns"
            :data-source="list"
            :pagination="false"
            :scroll="{ y: 260 }"
            row-key="id"
          >
            <template slot="money" slot-scope="money, record">
              <span :class="{ 'payment-amount--deduct': record.type === 1 }">
                {{ record.type === 1 ? '-' : '+' }}￥{{ formatMoney(money) }}
              </span>
            </template>
          </a-table>
          <a-empty v-else description="暂无支付项" :image="emptyImage" />
          <div v-if="list.length > 0" class="payment-summary">
            共 {{ list.length }} 项，合计：<span class="payment-summary-amount">{{ formatMoney(paymentItemsTotal) }}</span> 元
          </div>
        </a-col>
        <a-col :span="14">
          <div class="payment-block-title">支付记录</div>
          <a-table
            v-if="recordList.length > 0"
            :columns="recordColumns"
            :data-source="recordList"
            :pagination="false"
            :scroll="{ y: 260 }"
            row-key="id"
          >
            <template slot="amount" slot-scope="amount">
              ￥{{ formatMoney(amount) }}
            </template>
            <template slot="status" slot-scope="status">
              {{ payStatusText(status) }}
            </template>
            <template slot="successTime" slot-scope="successTime">
              {{ successTime ? formatDateTime(successTime) : '-' }}
            </template>
          </a-table>
          <a-empty v-else description="暂无支付记录" :image="emptyImage" />
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
import { getOrderPaymentItems, getOrderPaymentRecords } from '@/api/order/order'

const PAY_STATUS_MAP = {
  0: '未支付',
  10: '支付成功',
  20: '支付退款',
  30: '已关闭'
}

export default {
  name: 'PaymentInfo',
  props: {
    orderId: { type: [Number, String], default: null }
  },
  data () {
    return {
      hasData: false,
      loading: false,
      list: [],
      recordList: [],
      emptyImage: undefined,
      columns: [
        { title: '名称', dataIndex: 'name', key: 'name', ellipsis: true },
        { title: '金额', dataIndex: 'money', key: 'money', width: 140, scopedSlots: { customRender: 'money' } }
      ],
      recordColumns: [
        { title: '支付单号', dataIndex: 'payOrderNo', key: 'payOrderNo', ellipsis: true },
        { title: '标题', dataIndex: 'subject', key: 'subject', width: 100, ellipsis: true },
        { title: '金额', dataIndex: 'amount', key: 'amount', width: 100, scopedSlots: { customRender: 'amount' } },
        { title: '状态', dataIndex: 'status', key: 'status', width: 90, scopedSlots: { customRender: 'status' } },
        { title: '渠道', dataIndex: 'channelCode', key: 'channelCode', width: 90, ellipsis: true },
        { title: '支付成功时间', dataIndex: 'successTime', key: 'successTime', width: 170, scopedSlots: { customRender: 'successTime' } }
      ]
    }
  },
  computed: {
    paymentItemsTotal () {
      return this.list.reduce((total, item) => {
        const money = this.toNumber(item.money)
        return total + (item.type === 1 ? -money : money)
      }, 0)
    }
  },
  watch: {
    orderId: {
      immediate: true,
      handler (id) {
        if (id) this.fetchAll()
        else {
          this.list = []
          this.recordList = []
          this.hasData = false
        }
      }
    }
  },
  methods: {
    async fetchAll () {
      if (!this.orderId) return
      this.loading = true
      this.hasData = false
      try {
        const [itemsRes, recordsRes] = await Promise.all([
          getOrderPaymentItems(this.orderId),
          getOrderPaymentRecords(this.orderId)
        ])
        const list = (itemsRes && itemsRes.data) ? itemsRes.data : []
        const recordList = (recordsRes && recordsRes.data) ? recordsRes.data : []
        this.list = list
        this.recordList = Array.isArray(recordList) ? recordList : []
        this.hasData = true
      } catch (e) {
        this.list = []
        this.recordList = []
        this.hasData = false
      } finally {
        this.loading = false
      }
    },
    payStatusText (status) {
      return PAY_STATUS_MAP[status] != null ? PAY_STATUS_MAP[status] : (status != null ? String(status) : '-')
    },
    toNumber (value) {
      const amount = Number(value)
      return Number.isNaN(amount) ? 0 : amount
    },
    formatMoney (value) {
      return this.toNumber(value).toFixed(2)
    },
    formatDateTime (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}:${s}`
    }
  }
}
</script>

<style lang="less" scoped>
.order-detail-tab {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.order-detail-tab .tab-content {
  width: 100%;
  padding: 16px;
  align-self: flex-start;
}
.payment-amount--deduct {
  color: #b7331f;
}
.payment-summary {
  margin-top: 12px;
  padding: 8px 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}
.payment-summary-amount {
  font-weight: 600;
  color: #ff5733;
}
.payment-block-title {
  margin-bottom: 8px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
</style>
